import twMerge from "../lib/twMerge";
import Box from "./Box";

export type Column<T> = {
  label: string;
  width: number;
  getCellContents: (row: T) => string;
};

export default function Table<T>({
  className,
  columns,
  rows,
}: {
  className?: string;
  columns: Column<T>[];
  rows: T[];
}): React.ReactElement {
  /**
   * Cells render a right and bottom border. We hide the right border of every
   * cell in the last column, and hide the bottom border of every cell in the
   * bottom row, to avoid clashing with the main table border.
   */
  return (
    <Box
      className={twMerge(
        "flex flex-col items-stretch overflow-x-auto",
        className
      )}
    >
      <Row>
        {columns.map((column) => (
          <Cell width={column.width}>{column.label}</Cell>
        ))}
        <Cell width={0} className="flex-grow" hideRightBorder />
      </Row>

      {rows.map((row) => (
        <Row>
          {columns.map((column, index) => (
            <Cell width={column.width}>{column.getCellContents(row)}</Cell>
          ))}
          <Cell width={0} className="flex-grow" hideRightBorder />
        </Row>
      ))}

      <Row className="flex-grow">
        {columns.map((column) => (
          <Cell width={column.width} growHeight hideBottomBorder />
        ))}
        <Cell
          width={0}
          className="flex-grow"
          hideRightBorder
          hideBottomBorder
          growHeight
        />
      </Row>
    </Box>
  );
}

function Row({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className={twMerge("flex flex-row items-stretch", className)}>
      {children}
    </div>
  );
}

function Cell({
  className,
  width,
  hideRightBorder,
  hideBottomBorder,
  growHeight,
  children,
}: {
  className?: string;
  width: number;
  hideRightBorder?: boolean;
  hideBottomBorder?: boolean;
  growHeight?: boolean;
  children?: React.ReactNode;
}): React.ReactElement {
  return (
    <div
      className={twMerge(
        "flex flex-shrink-0 flex-row items-center gap-2 overflow-auto whitespace-nowrap border-b border-r border-base-grey-3-light px-[12px] py-[8px]",
        className,
        hideRightBorder && "border-r-0",
        !growHeight && "h-[42px]",
        hideBottomBorder && "border-b-0"
      )}
      style={{ width }}
    >
      {children}
    </div>
  );
}
